import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Bell, Mail, Smartphone, X, Globe, User, Check, Settings,
  Search, ChevronDown, Plus, Info, AlertTriangle, AlertCircle, CheckCircle2,
  Filter, Layers, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { http } from '@/libs/http';
import { PageLayout } from '@/components/page-layout';

// ─── Types ────────────────────────────────────────────────────────────────────

// ─── Types ────────────────────────────────────────────────────────────────────

interface FilterRules {
  tenants: string[];
  users: string[];
}

interface NotificationFilter {
  include: FilterRules;
  exclude: FilterRules;
}

interface NotificationPreference {
  event_id: string;
  level: string;
  type: string;
  description: string;
  notify?: boolean;
  email_subscribed: boolean;
  push_subscribed: boolean;
  filters?: NotificationFilter | null;
}

interface TenantItem { id: string; name: string; slug: string; }
interface UserItem { id: string; name: string; email: string; }

// ─── Helpers ─────────────────────────────────────────────────────────────────

const levelConfig = {
  platform: { label: 'Platform', color: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300', icon: Layers },
  tenant:   { label: 'Tenant',   color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',       icon: Globe },
  user:     { label: 'User',     color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', icon: User },
};

const typeConfig: Record<string, { color: string; icon: React.FC<any> }> = {
  success: { color: 'text-emerald-600 dark:text-emerald-400', icon: CheckCircle2 },
  error:   { color: 'text-red-600 dark:text-red-400',         icon: AlertCircle },
  warning: { color: 'text-amber-600 dark:text-amber-400',     icon: AlertTriangle },
  info:    { color: 'text-sky-600 dark:text-sky-400',         icon: Info },
};

// ─── Multi-Select Tag Input ────────────────────────────────────────────────────

const TagInput = ({
  label,
  icon: Icon,
  selected,
  options,
  onAdd,
  onRemove,
  customPlaceholder,
  emptyLabel,
}: {
  label: string;
  icon: React.FC<any>;
  selected: string[];
  options: { id: string; label: string; value: string; sublabel?: string }[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  customPlaceholder: string;
  emptyLabel: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [customInput, setCustomInput] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(
    o => !selected.includes(o.value) &&
      (o.label.toLowerCase().includes(query.toLowerCase()) ||
       (o.sublabel || '').toLowerCase().includes(query.toLowerCase()))
  );

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>

      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5">
        {selected.length === 0 ? (
          <span className="text-xs italic text-slate-400 dark:text-slate-500">{emptyLabel}</span>
        ) : (
          selected.map(v => (
            <span
              key={v}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
            >
              {v}
              <button
                type="button"
                onClick={() => onRemove(v)}
                className="w-3.5 h-3.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))
        )}
      </div>

      {/* Add button + popover */}
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => { setOpen(v => !v); setQuery(''); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-(--primary) hover:text-(--primary) transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Add {label.split(' ').pop()}
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-2 z-50 w-72 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl overflow-hidden">
            {/* Search */}
            <div className="p-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-(--primary) text-slate-900 dark:text-white"
                />
              </div>
            </div>

            {/* Options list */}
            <div className="max-h-44 overflow-y-auto">
              {filtered.length === 0 && !query ? (
                <p className="px-4 py-3 text-xs text-slate-400 text-center italic">All available options selected</p>
              ) : filtered.length === 0 ? (
                <p className="px-4 py-3 text-xs text-slate-400 text-center italic">No matches for "{query}"</p>
              ) : (
                filtered.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => { onAdd(opt.value); setQuery(''); }}
                    className="w-full text-left px-3.5 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors flex flex-col"
                  >
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{opt.label}</span>
                    {opt.sublabel && <span className="text-[10px] text-slate-400">{opt.sublabel}</span>}
                  </button>
                ))
              )}
            </div>

            {/* Custom input */}
            <div className="p-2.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={customPlaceholder}
                  value={customInput}
                  onChange={e => setCustomInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (customInput.trim()) { onAdd(customInput.trim()); setCustomInput(''); }
                    }
                  }}
                  className="flex-1 px-2.5 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-1 focus:ring-(--primary) text-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => { if (customInput.trim()) { onAdd(customInput.trim()); setCustomInput(''); } }}
                  className="px-2.5 py-1.5 bg-(--primary) text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Toggle Switch ────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, label, icon: Icon, activeColor }: {
  checked: boolean;
  onChange: () => void;
  label: string;
  icon: React.FC<any>;
  activeColor: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 w-full ${
      checked
        ? `${activeColor} shadow-sm`
        : 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 text-slate-400 hover:border-slate-300 dark:hover:border-slate-700'
    }`}
  >
    <Icon className="w-4 h-4 shrink-0" />
    <span className="text-sm font-semibold flex-1 text-left">{label}</span>
    {/* pill switch */}
    <span className={`relative inline-flex w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${checked ? 'bg-current opacity-100' : 'bg-slate-300 dark:bg-slate-700 opacity-70'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : ''}`} />
    </span>
  </button>
);

// ─── Event Card ───────────────────────────────────────────────────────────────

const EventCard = ({
  pref,
  tenantOptions,
  userOptions,
  onToggleChannel,
  onAddFilter,
  onRemoveFilter,
}: {
  pref: NotificationPreference;
  tenantOptions: { id: string; label: string; value: string; sublabel?: string }[];
  userOptions: { id: string; label: string; value: string; sublabel?: string }[];
  onToggleChannel: (channel: 'email' | 'push') => void;
  onAddFilter: (mode: 'include' | 'exclude', target: 'tenants' | 'users', value: string) => void;
  onRemoveFilter: (mode: 'include' | 'exclude', target: 'tenants' | 'users', value: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const TypeIcon = typeConfig[pref.type]?.icon ?? Info;
  const lCfg = levelConfig[pref.level as keyof typeof levelConfig];
  const LevelIcon = lCfg?.icon ?? Zap;
  const isSubscribed = pref.email_subscribed || pref.push_subscribed;

  const incTenants = pref.filters?.include?.tenants || [];
  const excTenants = pref.filters?.exclude?.tenants || [];
  const incUsers = pref.filters?.include?.users || [];
  const excUsers = pref.filters?.exclude?.users || [];

  return (
    <div className={`group rounded-2xl border transition-all duration-200 ${
      isSubscribed
        ? 'border-(--primary)/20 bg-white dark:bg-slate-950 shadow-sm'
        : 'border-slate-100 dark:border-slate-900 bg-white dark:bg-slate-950 hover:border-slate-200 dark:hover:border-slate-800'
    }`}>
      {/* Card header — always visible */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Type icon */}
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
          isSubscribed ? 'bg-(--primary)/10' : 'bg-slate-100 dark:bg-slate-900'
        }`}>
          <TypeIcon className={`w-4.5 h-4.5 ${isSubscribed ? 'text-(--primary)' : typeConfig[pref.type]?.color ?? 'text-slate-400'}`} />
        </div>

        {/* Event identity */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-white truncate">{pref.event_id}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${lCfg?.color}`}>
              <LevelIcon className="w-2.5 h-2.5" />
              {lCfg?.label}
            </span>
            {pref.notify === false && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50 uppercase tracking-wide shrink-0">
                Notifications Disabled
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{pref.description}</p>
        </div>

        {/* Compact channel indicators */}
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg transition-all ${
            pref.email_subscribed
              ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
              : 'bg-slate-100 dark:bg-slate-900 text-slate-400 dark:text-slate-600'
          }`}>
            <Mail className="w-3 h-3" />
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg transition-all ${
            pref.push_subscribed
              ? 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400'
              : 'bg-slate-100 dark:bg-slate-900 text-slate-400 dark:text-slate-600'
          }`}>
            <Smartphone className="w-3 h-3" />
          </span>
        </div>

        {/* Expand toggle */}
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-900 text-slate-400 transition-all shrink-0"
        >
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-900 px-5 pt-5 pb-6 space-y-6 animate-in fade-in slide-in-from-top-1 duration-150">
          
          {pref.notify === false && (
            <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl text-amber-800 dark:text-amber-300 text-xs font-semibold">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>Notifications Disabled by Admin — Subscriptions for this event are currently paused system-wide. Activity logging remains active if configured.</span>
            </div>
          )}

          {/* Channels section */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Notification Channels</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Toggle
                checked={pref.email_subscribed}
                onChange={() => onToggleChannel('email')}
                label="Email"
                icon={Mail}
                activeColor="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400"
              />
              <Toggle
                checked={pref.push_subscribed}
                onChange={() => onToggleChannel('push')}
                label="Push Notification"
                icon={Smartphone}
                activeColor="bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400"
              />
            </div>
          </div>

          {/* Audience Filters */}
          {pref.level === 'platform' ? (
            <div className="flex items-center gap-2.5 px-3.5 py-3 bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800 rounded-xl">
              <Globe className="w-4 h-4 text-violet-500 shrink-0" />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Platform-level events are broadcast to all targets — no audience filter applies.
              </p>
            </div>
          ) : (
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-slate-900">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Audience Filters — Inclusion & Exclusion
                </h4>
                <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                  <Filter className="w-3 h-3" />
                  Targeting Rules
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Include Block */}
                <div className="p-4 rounded-xl bg-emerald-50/40 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/40 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Inclusion Rules (Allow List)
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Only trigger notifications when event matches specified targets.
                  </p>

                  {pref.level === 'tenant' && (
                    <TagInput
                      label="Included Tenants"
                      icon={Globe}
                      selected={incTenants}
                      options={tenantOptions}
                      onAdd={v => onAddFilter('include', 'tenants', v)}
                      onRemove={v => onRemoveFilter('include', 'tenants', v)}
                      customPlaceholder="Type tenant slug & press Enter"
                      emptyLabel="All tenants included (no filter)"
                    />
                  )}

                  {pref.level === 'user' && (
                    <TagInput
                      label="Included Users"
                      icon={User}
                      selected={incUsers}
                      options={userOptions}
                      onAdd={v => onAddFilter('include', 'users', v)}
                      onRemove={v => onRemoveFilter('include', 'users', v)}
                      customPlaceholder="Type email & press Enter"
                      emptyLabel="All users included (no filter)"
                    />
                  )}
                </div>

                {/* Exclude Block */}
                <div className="p-4 rounded-xl bg-red-50/40 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-red-700 dark:text-red-300">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    Exclusion Rules (Block List)
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Suppress notifications when event matches these targets.
                  </p>

                  {pref.level === 'tenant' && (
                    <TagInput
                      label="Excluded Tenants"
                      icon={Globe}
                      selected={excTenants}
                      options={tenantOptions}
                      onAdd={v => onAddFilter('exclude', 'tenants', v)}
                      onRemove={v => onRemoveFilter('exclude', 'tenants', v)}
                      customPlaceholder="Type tenant slug & press Enter"
                      emptyLabel="No tenants excluded"
                    />
                  )}

                  {pref.level === 'user' && (
                    <TagInput
                      label="Excluded Users"
                      icon={User}
                      selected={excUsers}
                      options={userOptions}
                      onAdd={v => onAddFilter('exclude', 'users', v)}
                      onRemove={v => onRemoveFilter('exclude', 'users', v)}
                      customPlaceholder="Type email & press Enter"
                      emptyLabel="No users excluded"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const PreferencesPage = () => {
  const { setBreadcrumbs } = useLayout();
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'notifications' | 'general'>('notifications');

  const [tenantsList, setTenantsList] = useState<TenantItem[]>([]);
  const [usersList, setUsersList] = useState<UserItem[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'platform' | 'tenant' | 'user'>('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const prefs = await http.get<NotificationPreference[]>('/iam/v1/users/preferences/notifications');
      setPreferences(prefs || []);

      try {
        const tenants = await http.get<any>('/mgmt/v1/tenants');
        setTenantsList(tenants?.items || tenants || []);
      } catch { /* silent */ }

      try {
        const users = await http.get<any>('/mgmt/v1/users');
        setUsersList(users?.items || users || []);
      } catch { /* silent */ }

    } catch {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBreadcrumbs([{ name: 'Preferences' }]);
    fetchData();
  }, [setBreadcrumbs]);

  const update = useCallback((eventId: string, updater: (p: NotificationPreference) => NotificationPreference) => {
    setPreferences(prev => prev.map(p => p.event_id === eventId ? updater(p) : p));
  }, []);

  const handleToggleChannel = (eventId: string, channel: 'email' | 'push') =>
    update(eventId, p => ({ ...p, [`${channel}_subscribed`]: !p[`${channel}_subscribed`] }));

  const handleAddFilter = (
    eventId: string,
    mode: 'include' | 'exclude',
    target: 'tenants' | 'users',
    value: string
  ) => {
    update(eventId, p => {
      const currentFilters = p.filters || {
        include: { tenants: [], users: [] },
        exclude: { tenants: [], users: [] },
      };
      const modeObj = currentFilters[mode] || { tenants: [], users: [] };
      const list = modeObj[target] || [];
      if (list.includes(value)) return p;

      return {
        ...p,
        filters: {
          ...currentFilters,
          [mode]: {
            ...modeObj,
            [target]: [...list, value],
          },
        },
      };
    });
  };

  const handleRemoveFilter = (
    eventId: string,
    mode: 'include' | 'exclude',
    target: 'tenants' | 'users',
    value: string
  ) => {
    update(eventId, p => {
      if (!p.filters) return p;
      const modeObj = p.filters[mode] || { tenants: [], users: [] };
      const list = modeObj[target] || [];

      return {
        ...p,
        filters: {
          ...p.filters,
          [mode]: {
            ...modeObj,
            [target]: list.filter(v => v !== value),
          },
        },
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = preferences.map(({ event_id, email_subscribed, push_subscribed, filters }) => ({
        event_id,
        email_subscribed,
        push_subscribed,
        filters: filters || {
          include: { tenants: [], users: [] },
          exclude: { tenants: [], users: [] },
        },
      }));
      const updated = await http.put<NotificationPreference[]>('/iam/v1/users/preferences/notifications', payload);
      setPreferences(updated || []);
      toast.success('Preferences saved successfully');
    } catch {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const filteredPreferences = useMemo(() =>
    preferences.filter(p => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || p.event_id.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
      const matchLevel = levelFilter === 'all' || p.level === levelFilter;
      return matchSearch && matchLevel;
    }),
  [preferences, searchQuery, levelFilter]);

  const tenantOptions = useMemo(() =>
    tenantsList.map(t => ({ id: t.id, label: t.name, value: t.slug, sublabel: t.slug })),
  [tenantsList]);

  const userOptions = useMemo(() =>
    usersList.map(u => ({ id: u.id, label: u.name || u.email, value: u.email, sublabel: u.email })),
  [usersList]);

  // Stats
  const stats = useMemo(() => ({
    subscribed: preferences.filter(p => p.email_subscribed || p.push_subscribed).length,
    email: preferences.filter(p => p.email_subscribed).length,
    push: preferences.filter(p => p.push_subscribed).length,
    total: preferences.length,
  }), [preferences]);

  if (loading) {
    return (
      <PageLayout title="Preferences" description="Manage your personal preferences and notification settings.">
        <div className="flex justify-center items-center h-64"><Loader /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Preferences"
      description="Manage your personal settings, notification events and delivery channels."
    >
      <div className="flex flex-col lg:flex-row gap-8 animate-in fade-in duration-300">

        {/* ── Sidebar ── */}
        <aside className="w-full lg:w-60 shrink-0 space-y-2">
          {[
            { id: 'notifications', label: 'Notifications', icon: Bell },
            { id: 'general',       label: 'General',       icon: Settings },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as any)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-150 ${
                activeTab === id
                  ? 'bg-(--primary) text-white shadow-md'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900/60'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </aside>

        {/* ── Main panel ── */}
        <div className="flex-1 min-w-0 space-y-6">

          {activeTab === 'general' ? (
            <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-900 p-8 space-y-4">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">General Preferences</h2>
              <p className="text-sm text-slate-500">Adjust default preferences for your session and profile settings.</p>
              <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-xl p-10 text-center text-slate-400 dark:text-slate-500 text-sm italic">
                No general preference settings available currently.
              </div>
            </div>
          ) : (
            <>
              {/* ── Stats strip ── */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Events', value: stats.total, color: 'text-slate-700 dark:text-slate-300', iconColor: 'bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400', Icon: Zap },
                  { label: 'Subscribed',   value: stats.subscribed, color: 'text-(--primary)', iconColor: 'bg-orange-50 dark:bg-orange-950/30 text-(--primary)', Icon: Bell },
                  { label: 'Email',        value: stats.email, color: 'text-blue-600 dark:text-blue-400', iconColor: 'bg-blue-50 dark:bg-blue-950/30 text-blue-500 dark:text-blue-400', Icon: Mail },
                  { label: 'Push',         value: stats.push, color: 'text-indigo-600 dark:text-indigo-400', iconColor: 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-500 dark:text-indigo-400', Icon: Smartphone },
                ].map(({ label, value, color, iconColor, Icon: StatIcon }) => (
                  <div key={label} className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl px-5 py-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
                      <StatIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className={`text-2xl font-black leading-none ${color}`}>{value}</div>
                      <div className="text-xs text-slate-400 dark:text-slate-500 font-medium mt-1">{label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Toolbar ── */}
              <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Save button — left aligned */}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-(--primary) text-white rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-all shadow-sm shrink-0"
                >
                  {saving
                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Check className="w-4 h-4" />}
                  Save Preferences
                </button>

                <div className="flex-1" />

                {/* Search */}
                <div className="relative max-w-sm w-full sm:w-auto">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-xl text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-(--primary)/20 focus:border-(--primary) transition-all"
                  />
                </div>

                {/* Level filter segmented control */}
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-xl gap-0.5">
                    {(['all', 'platform', 'tenant', 'user'] as const).map(level => (
                      <button
                        key={level}
                        onClick={() => setLevelFilter(level)}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all duration-150 ${
                          levelFilter === level
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Event cards ── */}
              <div className="space-y-2">
                {filteredPreferences.length === 0 ? (
                  <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl py-16 text-center text-slate-400 dark:text-slate-500 text-sm italic">
                    No events match your current filters.
                  </div>
                ) : (
                  filteredPreferences.map(pref => (
                    <EventCard
                      key={pref.event_id}
                      pref={pref}
                      tenantOptions={tenantOptions}
                      userOptions={userOptions}
                      onToggleChannel={channel => handleToggleChannel(pref.event_id, channel)}
                      onAddFilter={(mode, target, v) => handleAddFilter(pref.event_id, mode, target, v)}
                      onRemoveFilter={(mode, target, v) => handleRemoveFilter(pref.event_id, mode, target, v)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
